/**
 * Generato automaticamente.
 * framework-cfg-procedimenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetVersioniProcedimentoEsportaParams,
  PostVersioniProcedimentoImportaBody,
  PostVersioniProcedimentoImportaParams,
  WsProblema,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Importa una versione di un procedimento
 * @summary Importa una versione di un procedimento
 */
export const postVersioniProcedimentoImporta = (
  postVersioniProcedimentoImportaBody: PostVersioniProcedimentoImportaBody,
  params?: PostVersioniProcedimentoImportaParams,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  const formData = new FormData();
  formData.append('file', postVersioniProcedimentoImportaBody.file);

  return apiClientRequest<unknown>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/importa`,
      method: 'post',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      params,
    },
    options,
  );
};

export const getPostVersioniProcedimentoImportaMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoImporta>>,
    TError,
    {
      data: PostVersioniProcedimentoImportaBody;
      params?: PostVersioniProcedimentoImportaParams;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postVersioniProcedimentoImporta>>,
  TError,
  {
    data: PostVersioniProcedimentoImportaBody;
    params?: PostVersioniProcedimentoImportaParams;
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postVersioniProcedimentoImporta>>,
    {
      data: PostVersioniProcedimentoImportaBody;
      params?: PostVersioniProcedimentoImportaParams;
    }
  > = props => {
    const { data, params } = props ?? {};

    return postVersioniProcedimentoImporta(data, params, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostVersioniProcedimentoImportaMutationResult = NonNullable<
  Awaited<ReturnType<typeof postVersioniProcedimentoImporta>>
>;
export type PostVersioniProcedimentoImportaMutationBody =
  PostVersioniProcedimentoImportaBody;
export type PostVersioniProcedimentoImportaMutationError =
  ErrorType<WsProblema>;

/**
 * @summary Importa una versione di un procedimento
 */
export const usePostVersioniProcedimentoImporta = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postVersioniProcedimentoImporta>>,
    TError,
    {
      data: PostVersioniProcedimentoImportaBody;
      params?: PostVersioniProcedimentoImportaParams;
    },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPostVersioniProcedimentoImportaMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Esporta una versione di un procedimento
 * @summary Esporta una versione di un procedimento
 */
export const getVersioniProcedimentoEsporta = (
  params: GetVersioniProcedimentoEsportaParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<unknown>(
    {
      url: `/framework-cfg-procedimenti/versioni-procedimento/esporta`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetVersioniProcedimentoEsportaQueryKey = (
  params: GetVersioniProcedimentoEsportaParams,
) =>
  [
    `/framework-cfg-procedimenti/versioni-procedimento/esporta`,
    ...(params ? [params] : []),
  ] as const;

export const getGetVersioniProcedimentoEsportaQueryOptions = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoEsporta>>,
  TError = ErrorType<WsProblema>,
>(
  params: GetVersioniProcedimentoEsportaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoEsporta>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getVersioniProcedimentoEsporta>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetVersioniProcedimentoEsportaQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getVersioniProcedimentoEsporta>>
  > = ({ signal }) =>
    getVersioniProcedimentoEsporta(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetVersioniProcedimentoEsportaQueryResult = NonNullable<
  Awaited<ReturnType<typeof getVersioniProcedimentoEsporta>>
>;
export type GetVersioniProcedimentoEsportaQueryError = ErrorType<WsProblema>;

/**
 * @summary Esporta una versione di un procedimento
 */
export const useGetVersioniProcedimentoEsporta = <
  TData = Awaited<ReturnType<typeof getVersioniProcedimentoEsporta>>,
  TError = ErrorType<WsProblema>,
>(
  params: GetVersioniProcedimentoEsportaParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getVersioniProcedimentoEsporta>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetVersioniProcedimentoEsportaQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
